import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useClient } from '@/plugins/api.js';
import { jwtDecode } from 'jwt-decode';
import { formatDuration } from '@/internal_time_ago.js';

const authStorageKeys = {
    accessToken: 'auth-accessToken',
    refreshToken: 'auth-refreshToken',
    tokenClaims: 'auth-tokenClaims',
    rememberMe: 'auth-rememberMe',
};

function getBrowserStorage(type) {
    if (typeof window === 'undefined') {
        return null;
    }

    return type === 'local' ? window.localStorage : window.sessionStorage;
}

function readJsonStorage(storage, key) {
    if (!storage) {
        return null;
    }

    const value = storage.getItem(key);
    try {
        return value ? JSON.parse(value) : null;
    } catch {
        storage.removeItem(key);
        return null;
    }
}

function storageHasAuth(storage) {
    return Boolean(storage?.getItem(authStorageKeys.accessToken) && storage?.getItem(authStorageKeys.refreshToken));
}

function readStoredAuth() {
    const localStorage = getBrowserStorage('local');
    const sessionStorage = getBrowserStorage('session');
    const rememberMe = localStorage?.getItem(authStorageKeys.rememberMe) === 'true' && storageHasAuth(localStorage);
    const storage = rememberMe ? localStorage : storageHasAuth(sessionStorage) ? sessionStorage : null;

    return {
        accessToken: storage?.getItem(authStorageKeys.accessToken) ?? undefined,
        refreshToken: storage?.getItem(authStorageKeys.refreshToken) ?? undefined,
        tokenClaims: readJsonStorage(storage, authStorageKeys.tokenClaims),
        rememberMe,
    };
}

function clearStoredAuth() {
    for (const storage of [getBrowserStorage('local'), getBrowserStorage('session')]) {
        storage?.removeItem(authStorageKeys.accessToken);
        storage?.removeItem(authStorageKeys.refreshToken);
        storage?.removeItem(authStorageKeys.tokenClaims);
        storage?.removeItem(authStorageKeys.rememberMe);
    }
}

export const useAuthenticationStore = defineStore('authentication', () => {
    const clientApi = useClient();
    const router = useRouter();

    const isRefreshing = ref(false);
    let refreshPromise = null;
    const storedAuth = readStoredAuth();

    const accessToken = ref(storedAuth.accessToken);
    const refreshToken = ref(storedAuth.refreshToken);
    const tokenClaims = ref(storedAuth.tokenClaims);
    const rememberMe = ref(storedAuth.rememberMe);

    const isAuthenticated = computed(() => !!accessToken.value);
    const userId = computed(() => tokenClaims.value?.sub);
    const userRoles = computed(() => {
        const claims = tokenClaims.value ?? {};
        const candidates = [
            claims.role,
            claims.roles,
            claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'],
        ]
            .flatMap(value => (Array.isArray(value) ? value : value ? [value] : []))
            .map(v => v.toLowerCase());

        return [...new Set(candidates)];
    });
    const persona = computed(() => tokenClaims.value?.persona ?? null);
    const isManager = computed(() => userRoles.value.includes('administrator') || userRoles.value.includes('manager'));
    const isClient = computed(() => userRoles.value.includes('client'));
    const isProvider = computed(() => userRoles.value.includes('provider'));
    const ownedOrganizationIds = ref([]);
    const selectedOrganizationOwnerId = ref(null);
    const isOwner = computed(() =>
        Boolean(
            userId.value &&
            selectedOrganizationOwnerId.value &&
            String(selectedOrganizationOwnerId.value).toLowerCase() === String(userId.value).toLowerCase()
        )
    );

    function persistTokens() {
        clearStoredAuth();

        const storage = getBrowserStorage(rememberMe.value ? 'local' : 'session');
        if (!storage) {
            return;
        }

        storage.setItem(authStorageKeys.accessToken, accessToken.value);
        storage.setItem(authStorageKeys.refreshToken, refreshToken.value);
        storage.setItem(authStorageKeys.tokenClaims, JSON.stringify(tokenClaims.value));
        storage.setItem(authStorageKeys.rememberMe, String(rememberMe.value));
    }

    function updateTokens(data, options = {}) {
        if (!data?.accessToken || !data?.refreshToken) {
            throw new Error('Invalid token data');
        }
        if (typeof options.rememberMe === 'boolean') {
            rememberMe.value = options.rememberMe;
        }
        accessToken.value = data.accessToken;
        refreshToken.value = data.refreshToken;
        const claims = getClaimsFromToken(data.accessToken);
        tokenClaims.value = claims;
        persistTokens();
        console.log('Tokens updated, user ID:', claims?.sub);
    }

    function cleanTokens() {
        console.log('cleanTokens called - clearing stored tokens');
        accessToken.value = undefined;
        refreshToken.value = undefined;
        tokenClaims.value = null;
        rememberMe.value = false;
        ownedOrganizationIds.value = [];
        selectedOrganizationOwnerId.value = null;
        clearStoredAuth();
    }

    async function logout() {
        cleanTokens();
        await router.push('/');
    }

    async function login(email, password, options = {}) {
        console.log('login called with email:', email);
        if (!email || !password) {
            throw new Error('Email and password are required');
        }

        try {
            const response = await clientApi.post('api/users/login', {
                email: email.trim(),
                password: password,
                rememberMe: Boolean(options.rememberMe),
            });

            if (!response.data?.accessToken || !response.data?.refreshToken) {
                throw new Error('Invalid login response');
            }

            updateTokens(response.data, { rememberMe: Boolean(options.rememberMe) });
            console.log('login successful');
            return true;
        } catch (error) {
            console.error('Login failed:', error);
            cleanTokens();
            throw error;
        }
    }

    async function loginWithGoogle(accessTokenParam, options = {}) {
        console.log('loginWithGoogle called');
        if (!accessTokenParam) {
            throw new Error('Google access token is required');
        }

        try {
            const response = await clientApi.post('api/users/login-with-google', {
                accessToken: accessTokenParam,
                rememberMe: Boolean(options.rememberMe),
            });

            if (!response.data?.accessToken || !response.data?.refreshToken) {
                throw new Error('Invalid Google login response');
            }

            updateTokens(response.data, { rememberMe: Boolean(options.rememberMe) });
            console.log('Google login successful');
            return true;
        } catch (error) {
            console.error('Google login failed:', error);
            cleanTokens();
            throw error;
        }
    }

    async function loginWithFacebook(accessTokenParam, options = {}) {
        console.log('loginWithFacebook called');
        if (!accessTokenParam) {
            throw new Error('Facebook access token is required');
        }

        try {
            const response = await clientApi.post('api/users/login-with-facebook', {
                accessToken: accessTokenParam,
                rememberMe: Boolean(options.rememberMe),
            });

            if (!response.data?.accessToken || !response.data?.refreshToken) {
                throw new Error('Invalid Facebook login response');
            }

            updateTokens(response.data, { rememberMe: Boolean(options.rememberMe) });
            console.log('Facebook login successful');
            return true;
        } catch (error) {
            console.error('Facebook login failed:', error);
            cleanTokens();
            throw error;
        }
    }

    async function refresh() {
        console.log('refresh called');

        if (!refreshToken.value) {
            cleanTokens(); // Clear tokens first
            throw new Error('No refresh token available');
        }

        if (isRefreshing.value && refreshPromise) {
            console.log('Already refreshing, returning existing refreshPromise');
            return refreshPromise;
        }

        try {
            isRefreshing.value = true;
            refreshPromise = (async () => {
                try {
                    console.log('Sending refresh request...');

                    const response = await clientApi.post('api/users/refresh', {
                        refreshToken: refreshToken.value,
                        rememberMe: rememberMe.value,
                    });

                    if (!response.data?.accessToken || !response.data?.refreshToken) {
                        throw new Error('Invalid refresh response');
                    }

                    updateTokens({
                        accessToken: response.data.accessToken,
                        refreshToken: response.data.refreshToken,
                    });

                    console.log('Token refresh successful');
                    return true;
                } catch (error) {
                    console.error('Token refresh failed:', error);
                    cleanTokens();

                    const currentRoute = router.currentRoute.value;
                    const returnUrl = currentRoute.fullPath;

                    // Handle navigation
                    router
                        .push({
                            name: 'login',
                            query: { returnUrl },
                        })
                        .catch(navError => {
                            console.error('Navigation error after token refresh failure:', navError);
                        });

                    throw error; // Re-throw to notify callers
                }
            })();

            return await refreshPromise;
        } catch (error) {
            throw error;
        } finally {
            // Ensure these are always reset, even if an error is thrown
            isRefreshing.value = false;
            refreshPromise = null;
        }
    }

    function getClaimsFromToken(token) {
        if (!token) return null;
        try {
            return jwtDecode(token);
        } catch (error) {
            console.error('Failed to decode token:', error);
            return null;
        }
    }

    function isTokenExpiringSoon(token) {
        if (!token) {
            console.log('No token provided, considered expiring soon');
            return true;
        }

        const claims = getClaimsFromToken(token);
        if (!claims || !claims.exp) {
            console.log('No valid claims found, considered expiring soon');
            return true;
        }

        const expirationTime = claims.exp * 1000; // Convert to milliseconds
        const currentTime = Date.now();
        const fiveMinutesInMs = 2 * 60 * 1000; // 2 minutes for demonstration

        // Calculate time remaining (can be negative if already expired)
        const timeRemainingMs = expirationTime - currentTime;

        // Token is expiring soon if less than 2 minutes remaining or already expired
        const isExpiring = timeRemainingMs < fiveMinutesInMs;

        // Determine the sign for display purposes
        const formattedTimeRemaining =
            timeRemainingMs < 0 ? `-${formatDuration(Math.abs(timeRemainingMs))}` : formatDuration(timeRemainingMs);

        if (isExpiring) {
            console.log(`Token expiration check; is token expired: ${isExpiring}`, {
                expirationTime: new Date(expirationTime).toLocaleString(),
                currentTime: new Date(currentTime).toLocaleString(),
                timeRemaining: formattedTimeRemaining,
            });
        }

        return isExpiring;
    }

    async function changePassword(newPassword) {
        console.log('changePassword called');
        if (!isAuthenticated.value) {
            throw new Error('User must be authenticated to change password');
        }

        if (!newPassword) {
            throw new Error('New password is required');
        }

        try {
            const response = await clientApi.post('api/users/set-password', {
                newPassword,
            });

            console.log('Password changed successfully');
            return true;
        } catch (error) {
            console.error('Password change failed:', error);
            throw error;
        }
    }

    function hasAnyRole(roles) {
        return roles.some(role => userRoles.value.includes(role));
    }

    function setOwnedOrganizationIds(organizationIds) {
        ownedOrganizationIds.value = [...new Set((organizationIds ?? []).filter(Boolean))];
    }

    function setSelectedOrganizationOwnerId(ownerId) {
        selectedOrganizationOwnerId.value = ownerId ?? null;
    }

    function ownsOrganization(organizationId) {
        return ownedOrganizationIds.value.some(id => String(id).toLowerCase() === String(organizationId).toLowerCase());
    }

    return {
        accessToken,
        refreshToken,
        isAuthenticated,
        userId,
        userRoles,
        persona,
        hasAnyRole,
        isManager,
        isClient,
        isProvider,
        isOwner,
        ownedOrganizationIds,
        isRefreshing,
        login,
        loginWithGoogle,
        loginWithFacebook,
        logout,
        refresh,
        isTokenExpiringSoon,
        changePassword,
        setOwnedOrganizationIds,
        setSelectedOrganizationOwnerId,
        ownsOrganization,
    };
});
